import { useCallback, useEffect, useState } from "react";
import { computeKillZoneRanges, detectInstitutionalMarkers } from "@/lib/killZones";
import { zonePaint, DEFAULT_ZONE_STYLES } from "@/lib/zoneStyles";

/**
 * Convert a hex color (#RRGGBB) to `rgba(r,g,b,alpha)`. Returns input unchanged
 * for non-hex strings, so it's safe to pass through user-supplied values.
 */
function hexToRgba(hex, alpha = 1) {
  if (!hex || !hex.startsWith("#")) return hex;
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Normalize a candle's `time` field to a millisecond timestamp usable by
 * klinecharts' `convertToPixel` API. Handles seconds/ms/ISO string.
 */
function toMs(t) {
  let v = t;
  if (typeof v === "string") v = new Date(v).getTime();
  else if (v < 1e12) v = v * 1000;
  return v;
}

/**
 * Custom hook — compute the SVG overlay rectangles/markers rendered on top of
 * a klinecharts pane. Extracted from `ProCandleChart` to keep the component
 * focused on rendering.
 *
 * Returned object contains 7 arrays consumed as-is by the SVG layer:
 *   { overlayRects, killZoneBands, instMarkers,
 *     structureBoxes, cisdMarkers, fvgBoxes, swingPoints, crtBoxes }
 *
 * @param {object}  args
 * @param {object}  args.chartRef       React ref pointing to the klinecharts instance
 * @param {object}  args.containerRef   React ref pointing to the chart DOM container
 * @param {Array}   args.candles        Raw candle payload
 * @param {object}  args.zones          Zone payload (ZII, ZRI, DP, RL) from /api/analyze
 * @param {object}  args.structure      Market-structure payload (OB, BB, CISD, FVG, swings, CRT)
 * @param {boolean} args.showKillZones  Toggle kill-zone rectangles
 * @param {boolean} args.showMarkers    Toggle institutional impulsion markers
 * @param {Set}     args.activeIndicators   Set of active indicator names (used to filter structure layers)
 * @param {object}  args.theme          Chart theme (for kill-zone colours)
 */
export default function useChartOverlays({
  chartRef,
  containerRef,
  candles,
  zones,
  structure,
  showKillZones,
  showMarkers,
  activeIndicators,
  theme,
  zoneStyles = DEFAULT_ZONE_STYLES,
}) {
  const [overlayRects, setOverlayRects] = useState([]);
  const [killZoneBands, setKillZoneBands] = useState([]);
  const [instMarkers, setInstMarkers] = useState([]);
  const [structureBoxes, setStructureBoxes] = useState([]);
  const [cisdMarkers, setCisdMarkers] = useState([]);
  const [fvgBoxes, setFvgBoxes] = useState([]);
  const [swingPoints, setSwingPoints] = useState([]);
  const [crtBoxes, setCrtBoxes] = useState([]);

  const recomputeOverlay = useCallback(() => {
    const empty = () => {
      setOverlayRects([]); setKillZoneBands([]); setInstMarkers([]);
      setStructureBoxes([]); setCisdMarkers([]); setFvgBoxes([]);
      setSwingPoints([]); setCrtBoxes([]);
    };
    if (!chartRef.current || !containerRef.current) { empty(); return; }
    if (!candles || !candles.length) { empty(); return; }

    const chart = chartRef.current;
    const width = containerRef.current.clientWidth;
    const paneOpts = { paneId: "candle_pane" };
    // Safe pixel-conversion: klinecharts can return NaN / -Infinity when the
    // chart is still initializing (candles applied but layout not laid out yet).
    // We validate the result and return null so the caller can skip the item.
    const isFiniteNumber = (n) => Number.isFinite(n);
    const isValidPoint = (p) => p && isFiniteNumber(p.x) && isFiniteNumber(p.y);
    const px = (t, v) => {
      if (!isFiniteNumber(v)) return null;
      const p = chart.convertToPixel([{ timestamp: t, value: v }], paneOpts);
      const pt = Array.isArray(p) ? p[0] : p;
      return isValidPoint(pt) ? pt : null;
    };

    // ---- ZII / ZRI / DP boxes (styles from user zoneStyles + unmitigated priority) ----
    const rects = [];
    const collect = (arr, kind) => (arr || []).forEach((z, idx) => {
      const startIdx = z.index;
      if (startIdx < 0 || startIdx >= candles.length) return;
      const c = candles[startIdx];
      const t = toMs(c.time ?? c.timestamp ?? c.t);
      const p1 = px(t, z.top);
      const p2 = px(t, z.bottom);
      if (!p1 || p1.x == null || p1.y == null || !p2 || p2.y == null) return;
      if (p1.x > width) return; // offscreen right — cull
      const paint = zonePaint(zoneStyles, kind, z.direction, z.mitigated);
      if (!paint) return; // hidden (mitigated + hide mode)
      const y = Math.min(p1.y, p2.y);
      const h = Math.abs(p2.y - p1.y);
      const x = Math.max(0, p1.x);
      const w = Math.max(0, width - x - 60);
      const fresh = z.mitigated === false;
      rects.push({ id: `${kind}-${idx}`, kind, x, y, w, h, color: paint.fill, stroke: paint.stroke, label: fresh ? `${kind} ★` : kind });
    });
    collect(activeIndicators?.has("ZII") ? zones?.ZII : null, "ZII");
    collect(activeIndicators?.has("ZRI") ? zones?.ZRI : null, "ZRI");
    collect(activeIndicators?.has("DP") ? zones?.DP : null, "DP");
    setOverlayRects(rects);

    // ---- Kill-zones ----
    if (showKillZones) {
      const kzRanges = computeKillZoneRanges(candles);
      const zoneColorFor = (id) => ({
        asia:   theme.kzAsia,
        london: theme.kzLondon,
        ny_am:  theme.kzNyAm,
        ny_pm:  theme.kzNyPm,
      }[id] || "#8b5cf6");
      const bands = [];
      kzRanges.forEach((r, idx) => {
        const isBull = r.bias === "bull";
        const startPrice = isBull ? r.openLow  : r.openHigh;
        const endPrice   = isBull ? r.closeHigh : r.closeLow;
        const topPrice = Math.max(startPrice, endPrice);
        const botPrice = Math.min(startPrice, endPrice);
        const tl = px(r.startTime * 1000, topPrice);
        const br = px(r.endTime * 1000,   botPrice);
        if (!tl || tl.x == null || tl.y == null || !br || br.x == null || br.y == null) return;
        const x = Math.min(tl.x, br.x);
        const w = Math.abs(br.x - tl.x);
        const y = Math.min(tl.y, br.y);
        const h = Math.abs(br.y - tl.y);
        const baseHex = zoneColorFor(r.zoneId);
        bands.push({
          id: `kz-${r.zoneId}-${idx}`,
          x, y, w, h,
          fill: hexToRgba(baseHex, 0.16),
          stroke: hexToRgba(baseHex, 0.6),
          labelX: x + w / 2,
          labelY: y - 3,
          label: `${r.label} ${isBull ? "▲" : "▼"}`,
        });
      });
      setKillZoneBands(bands);
    } else {
      setKillZoneBands([]);
    }

    // ---- Institutional impulsion markers ----
    if (showMarkers) {
      const markers = detectInstitutionalMarkers(candles, 1.5);
      const rendered = [];
      markers.forEach((m, idx) => {
        const c = candles[m.candleIndex];
        if (!c) return;
        const pTop = px(m.time * 1000, c.high);
        const pBot = px(m.time * 1000, c.low);
        if (!pTop || pTop.x == null || !pBot || pBot.y == null) return;
        rendered.push({ id: `mk-${idx}`, x: pTop.x - 4, yTop: pTop.y, yBot: pBot.y, side: m.side, zScore: m.zScore });
      });
      setInstMarkers(rendered);
    } else {
      setInstMarkers([]);
    }

    // ---- Market Structure (OB / BB / CISD / FVG / Swing / CRT) ----
    const wantOB = activeIndicators?.has("OB");
    const wantBB = activeIndicators?.has("BB");
    const wantCISD = activeIndicators?.has("CISD");
    const wantFVG = activeIndicators?.has("FVG");
    const wantSwing = activeIndicators?.has("SWING");
    const wantCRT = activeIndicators?.has("CRT");
    const wantUnmit = activeIndicators?.has("UNMIT");
    const structBoxes = [];
    const cisdArr = [];
    const fvgArr = [];
    const swingPts = [];
    const crtArr = [];

    if (structure && (wantOB || wantBB)) {
      const drawBox = (item, kind) => {
        const t = toMs(item.time);
        const p1 = px(t, item.top);
        const p2 = px(t, item.bottom);
        if (!p1 || p1.x == null || !p2 || p2.y == null) return;
        if (p1.x > width) return;
        const paint = zonePaint(zoneStyles, kind, item.direction, item.mitigated);
        if (!paint) return;
        const y = Math.min(p1.y, p2.y);
        const h = Math.abs(p2.y - p1.y);
        const x = Math.max(0, p1.x);
        const w = Math.max(0, width - x - 60);
        const isBull = item.direction === "bull";
        const fresh = item.mitigated === false;
        const label = `${kind} ${isBull ? "▲" : "▼"}${fresh ? " ★" : ""}`;
        structBoxes.push({ id: `${kind}-${item.index}`, x, y, w, h, color: paint.fill, stroke: paint.stroke, label });
      };
      if (wantOB) (structure.order_blocks || []).filter((o) => !wantUnmit || o.mitigated === false).forEach((o) => drawBox(o, "OB"));
      if (wantBB) (structure.breaker_blocks || []).forEach((b) => drawBox(b, "BB"));
    }

    if (structure && wantCISD) {
      (structure.cisd || []).forEach((c) => {
        const t = toMs(c.time);
        const pt = px(t, c.level);
        if (!pt || pt.x == null || pt.y == null) return;
        cisdArr.push({ id: `cisd-${c.index}`, x: pt.x, y: pt.y, direction: c.direction, level: c.level });
      });
    }

    if (structure && wantFVG) {
      (structure.fvg || []).filter((f) => !wantUnmit || f.mitigated === false).forEach((f) => {
        const t = toMs(f.time);
        const p1 = px(t, f.top);
        const p2 = px(t, f.bottom);
        if (!p1 || p1.x == null || !p2 || p2.y == null) return;
        if (p1.x > width) return;
        const paint = zonePaint(zoneStyles, "FVG", f.direction, f.mitigated);
        if (!paint) return;
        const y = Math.min(p1.y, p2.y);
        const h = Math.abs(p2.y - p1.y);
        const x = Math.max(0, p1.x);
        const w = Math.max(0, width - x - 60);
        const isBull = f.direction === "bull";
        const fresh = f.mitigated === false;
        fvgArr.push({
          id: `fvg-${f.index}`, x, y, w, h,
          color: paint.fill,
          stroke: paint.stroke,
          label: `FVG ${isBull ? "▲" : "▼"}${fresh ? " ★" : ""}`,
        });
      });
    }

    if (structure && wantSwing) {
      (structure.swings || []).forEach((s) => {
        const t = toMs(s.time);
        const pt = px(t, s.price);
        if (!pt || pt.x == null || pt.y == null) return;
        swingPts.push({ id: `sw-${s.index}`, x: pt.x, y: pt.y, type: s.type });
      });
    }

    if (structure && wantCRT) {
      (structure.crt || []).forEach((c) => {
        const t = toMs(c.time);
        const p1 = px(t, c.top);
        const p2 = px(t, c.bottom);
        if (!p1 || p1.x == null || !p2 || p2.y == null) return;
        if (p1.x < -60 || p1.x > width + 30) return;
        const isBull = c.status === "break_up";
        const paint = zonePaint(zoneStyles, "CRT", isBull ? "bull" : "bear", undefined);
        if (!paint) return;
        const y = Math.min(p1.y, p2.y);
        const h = Math.abs(p2.y - p1.y);
        crtArr.push({
          id: `crt-${c.index}`, x: p1.x - 30, y, w: 60, h,
          color: paint.fill,
          stroke: paint.stroke,
          label: `CRT ${isBull ? "▲" : "▼"}`,
        });
      });
    }

    setStructureBoxes(structBoxes);
    setCisdMarkers(cisdArr);
    setFvgBoxes(fvgArr);
    setSwingPoints(swingPts);
    setCrtBoxes(crtArr);
  }, [chartRef, containerRef, candles, zones, structure, showKillZones, showMarkers, activeIndicators, theme, zoneStyles]);

  // Recompute on data change + chart pan/zoom + container resize
  useEffect(() => {
    recomputeOverlay();
    if (!chartRef.current || !containerRef.current) return;
    const chart = chartRef.current;
    const handler = () => recomputeOverlay();
    chart.subscribeAction("onVisibleRangeChange", handler);
    chart.subscribeAction("onPaneDrag", handler);
    const ro = new ResizeObserver(() => { chart.resize?.(); recomputeOverlay(); });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [recomputeOverlay, chartRef, containerRef]);

  return {
    overlayRects,
    killZoneBands,
    instMarkers,
    structureBoxes,
    cisdMarkers,
    fvgBoxes,
    swingPoints,
    crtBoxes,
  };
}
